import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
// Using regular SVG icons instead of Heroicons to avoid dependency issues
const ChevronLeftIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
)

const ChevronRightIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
)

const ClockIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
)

const CalendarIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
)

const UserIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
)

const PhoneIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
  </svg>
)

const MailIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
)
import { API_BASE_URL } from '../utils/api'
import CalendlyStyleCalendar from './CalendlyStyleCalendar'

// Types
interface MeetingType {
  id: number
  name: string
  name_fr?: string
  description: string
  description_fr?: string
  duration: number
  color: string
  buffer_before: number
  buffer_after: number
}

interface TimeSlot {
  time: string
  available: boolean
}

interface BookingFormData {
  customer_first_name: string
  customer_last_name: string
  customer_email: string
  customer_phone: string
  notes: string
  timezone: string
}

interface AppointmentBookingProps {
  brokerSlug: string
  onBookingComplete?: (booking: any) => void
  className?: string
}

const AppointmentBooking: React.FC<AppointmentBookingProps> = ({
  brokerSlug,
  onBookingComplete,
  className = ''
}) => {
  const { t, i18n } = useTranslation()

  // State management
  const [currentStep, setCurrentStep] = useState<'meeting-type' | 'date-time' | 'details' | 'confirmation'>('meeting-type')
  const [meetingTypes, setMeetingTypes] = useState<MeetingType[]>([])
  const [selectedMeetingType, setSelectedMeetingType] = useState<MeetingType | null>(null)
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [selectedTime, setSelectedTime] = useState<string>('')
  const [availableSlots, setAvailableSlots] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [calendarLoading, setCalendarLoading] = useState(false)
  const [error, setError] = useState<string>('')
  const [brokerData, setBrokerData] = useState<any>(null)
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date())
  const [availableDates, setAvailableDates] = useState<Date[]>([])
  const [slotsLoading, setSlotsLoading] = useState(false)

  const [formData, setFormData] = useState<BookingFormData>({
    customer_first_name: '',
    customer_last_name: '',
    customer_email: '',
    customer_phone: '',
    notes: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  })

  // Detect language from URL parameters only if not already set (respect parent component language)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const langParam = urlParams.get('lang')
    
    // Only change language if explicitly requested via URL param
    if (langParam === 'fr' || langParam === 'en') {
      i18n.changeLanguage(langParam)
    }
    // Otherwise, respect the current i18n language (set by parent component like CustomForm)
  }, [i18n])

  // Get broker data and meeting types on mount
  useEffect(() => {
    console.log('🔧 DEBUG - AppointmentBooking: Component MOUNTED for slug:', brokerSlug)
    fetchBrokerData()
    fetchMeetingTypes()
    
    return () => {
      console.log('🔧 DEBUG - AppointmentBooking: Component UNMOUNTING for slug:', brokerSlug)
    }
  }, [brokerSlug])

  // Fetch month availability when meeting type or month changes
  useEffect(() => {
    if (selectedMeetingType && brokerData) {
      fetchMonthAvailability(currentMonth, selectedMeetingType.id)
    }
  }, [currentMonth, selectedMeetingType, brokerData])

  // Fetch time slots when date is selected
  useEffect(() => {
    if (selectedMeetingType && selectedDate && brokerData) {
      fetchAvailability(selectedDate, selectedMeetingType.id)
    }
  }, [selectedDate, selectedMeetingType, brokerData])

  const fetchBrokerData = async () => {
    try {
      console.log('🔧 DEBUG - AppointmentBooking: Fetching broker data for slug:', brokerSlug)
      const url = `${API_BASE_URL}/api/custom-forms/broker/${brokerSlug}`
      console.log('🔧 DEBUG - AppointmentBooking: Broker data URL:', url)
      
      const response = await fetch(url)
      console.log('🔧 DEBUG - AppointmentBooking: Broker response status:', response.status)
      
      if (response.ok) {
        const data = await response.json()
        console.log('🔧 DEBUG - AppointmentBooking: Broker data:', data)
        setBrokerData(data)
      } else {
        console.error('🔧 DEBUG - AppointmentBooking: Failed to fetch broker data:', response.status)
      }
    } catch (error) {
      console.error('🔧 DEBUG - AppointmentBooking: Error fetching broker data:', error)
    }
  }

  const fetchMeetingTypes = async () => {
    try {
      console.log('🔧 DEBUG - AppointmentBooking: Fetching meeting types for broker slug:', brokerSlug)
      setLoading(true)
      
      const url = `${API_BASE_URL}/api/custom-forms/broker/${brokerSlug}/meeting-types`
      console.log('🔧 DEBUG - AppointmentBooking: Fetching from URL:', url)
      
      const response = await fetch(url)
      console.log('🔧 DEBUG - AppointmentBooking: Response status:', response.status, response.statusText)
      
      if (response.ok) {
        const data = await response.json()
        console.log('🔧 DEBUG - AppointmentBooking: Meeting types data:', data)
        console.log('🔧 DEBUG - AppointmentBooking: About to set meeting types:', data.meeting_types || [])
        setMeetingTypes(data.meeting_types || [])
        console.log('🔧 DEBUG - AppointmentBooking: Meeting types set, about to set loading false')
        
        if (!data.meeting_types || data.meeting_types.length === 0) {
          console.log('🔧 DEBUG - AppointmentBooking: No meeting types found - setting error message')
          setError('No meeting types configured. Please contact the broker to set up appointment types.')
        }
      } else {
        const errorText = await response.text()
        console.error('🔧 DEBUG - AppointmentBooking: Failed to fetch meeting types:', response.status, errorText)
        setError(`Unable to load meeting types (Error ${response.status})`)
      }
    } catch (error) {
      console.error('🔧 DEBUG - AppointmentBooking: Error fetching meeting types:', error)
      setError('Unable to load meeting types - network error')
    } finally {
      console.log('🔧 DEBUG - AppointmentBooking: Finally block - setting loading to false')
      setLoading(false)
    }
  }

  const fetchMonthAvailability = async (month: Date, meetingTypeId: number) => {
    try {
      setCalendarLoading(true)
      console.log('🔧 DEBUG - AppointmentBooking: fetchMonthAvailability called for month:', month.toISOString())
      
      const firstDay = new Date(month.getFullYear(), month.getMonth(), 1)
      const lastDay = new Date(month.getFullYear(), month.getMonth() + 1, 0)
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      
      const availableDatesSet = new Set<string>()
      const promises: Promise<void>[] = []
      
      // Check each day in the month (starting from today)
      for (let d = new Date(Math.max(firstDay.getTime(), today.getTime())); d <= lastDay; d.setDate(d.getDate() + 1)) {
        const dateToCheck = new Date(d)
        const dateStr = dateToCheck.toISOString().split('T')[0]
        
        const promise = fetch(
          `${API_BASE_URL}/api/appointments/availability/${brokerData?.id}?date=${dateStr}&meeting_type_id=${meetingTypeId}`
        )
        .then(response => {
          console.log(`🔧 DEBUG - API Response for ${dateStr}:`, response.status, response.statusText)
          if (response.ok) {
            return response.json()
          }
          console.log(`🔧 DEBUG - API Error for ${dateStr}:`, response.status)
          return null
        })
        .then(data => {
          console.log(`🔧 DEBUG - API Data for ${dateStr}:`, data)
          if (data && data.debug) {
            console.log(`🔧 DEBUG - Debug info for ${dateStr}:`, data.debug)
          }
          if (data && data.available_slots && data.available_slots.length > 0) {
            console.log(`🔧 DEBUG - Found ${data.available_slots.length} slots for ${dateStr}`)
            availableDatesSet.add(dateStr)
          } else {
            console.log(`🔧 DEBUG - No slots available for ${dateStr}`, data)
          }
        })
        .catch(error => {
          console.error('🔧 DEBUG - Error checking availability for', dateStr, error)
        })
        
        promises.push(promise)
      }
      
      await Promise.all(promises)
      
      // Convert date strings back to Date objects
      const availableDatesArray = Array.from(availableDatesSet).map(dateStr => new Date(dateStr + 'T00:00:00'))
      setAvailableDates(availableDatesArray)
      
      console.log('🔧 DEBUG - AppointmentBooking: Available dates:', availableDatesArray.length)
      
    } catch (error) {
      console.error('Error fetching month availability:', error)
      setAvailableDates([])
    } finally {
      setCalendarLoading(false)
    }
  }

  const fetchAvailability = async (date: Date, meetingTypeId: number) => {
    try {
      console.log('🔧 DEBUG - AppointmentBooking: fetchAvailability called with:', { date: date.toISOString(), meetingTypeId, brokerDataId: brokerData?.id })
      setSlotsLoading(true)
      const dateStr = date.toISOString().split('T')[0]
      
      const response = await fetch(
        `${API_BASE_URL}/api/appointments/availability/${brokerData?.id}?date=${dateStr}&meeting_type_id=${meetingTypeId}`
      )
      
      if (response.ok) {
        const data = await response.json()
        setAvailableSlots(data.available_slots || [])
      } else {
        setAvailableSlots([])
      }
    } catch (error) {
      console.error('Error fetching availability:', error)
      setAvailableSlots([])
    } finally {
      setSlotsLoading(false)
    }
  }

  const handleBookAppointment = async () => {
    try {
      setLoading(true)
      setError('')

      if (!selectedMeetingType || !selectedTime || !formData.customer_first_name || !formData.customer_last_name || !formData.customer_email || !formData.customer_phone) {
        setError(t('appointment.fillAllFields', 'Please fill in all required fields'))
        return
      }

      const bookingData = {
        meeting_type_id: selectedMeetingType.id,
        customer_first_name: formData.customer_first_name,
        customer_last_name: formData.customer_last_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        start_time: selectedTime,
        timezone: formData.timezone,
        notes: formData.notes
      }

      const response = await fetch(`${API_BASE_URL}/api/appointments/book/${brokerSlug}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bookingData)
      })

      if (response.ok) {
        const result = await response.json()
        setCurrentStep('confirmation')
        if (onBookingComplete) {
          onBookingComplete(result.appointment)
        }
      } else {
        const errorData = await response.json()
        setError(errorData.message || 'Failed to book appointment')
      }
    } catch (error) {
      console.error('Error booking appointment:', error)
      setError('An unexpected error occurred. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleDateSelect = (date: Date) => {
    // Preserve scroll position
    const scrollPosition = window.pageYOffset || document.documentElement.scrollTop
    
    setSelectedDate(date)
    setSelectedTime('') // Clear selected time when date changes
    console.log('🔧 DEBUG - AppointmentBooking: Date selected:', date.toISOString())
    
    // Restore scroll position after state update
    setTimeout(() => {
      window.scrollTo(0, scrollPosition)
    }, 0)
  }

  const handleMonthChange = (direction: 'prev' | 'next') => {
    const newMonth = new Date(currentMonth)
    newMonth.setMonth(newMonth.getMonth() + (direction === 'next' ? 1 : -1))
    setCurrentMonth(newMonth)
    setSelectedDate(null) // Clear selected date when month changes
    setSelectedTime('') // Clear selected time when month changes
    console.log('🔧 DEBUG - AppointmentBooking: Month changed to:', newMonth.toISOString())
  }

  const formatDate = (date: Date) => {
    return date.toLocaleDateString(i18n.language === 'fr' ? 'fr-CA' : 'en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const formatTime = (timeSlot: string) => {
    return new Date(timeSlot).toLocaleTimeString(i18n.language === 'fr' ? 'fr-CA' : 'en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: i18n.language === 'fr' ? false : true
    })
  }

  const getDurationText = (minutes: number) => {
    if (minutes < 60) return `${minutes} min`
    const hours = Math.floor(minutes / 60)
    const remainingMinutes = minutes % 60
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`
  }

  const canProceedToNext = () => {
    switch (currentStep) {
      case 'meeting-type':
        return selectedMeetingType !== null
      case 'date-time':
        return selectedDate !== null && selectedTime !== ''
      case 'details':
        return formData.customer_first_name.trim() !== '' && formData.customer_last_name.trim() !== '' && formData.customer_email.trim() !== '' && formData.customer_phone.trim() !== ''
      default:
        return false
    }
  }

  if (!brokerData) {
    return (
      <div className={`max-w-md mx-auto p-6 bg-white rounded-xl shadow-lg ${className}`}>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-300 rounded w-3/4 mb-4"></div>
          <div className="h-4 bg-gray-300 rounded w-1/2"></div>
        </div>
      </div>
    )
  }

  console.log('🔧 DEBUG - AppointmentBooking: RENDER called with state:', { 
    loading, 
    meetingTypesCount: meetingTypes.length, 
    currentStep, 
    selectedMeetingType: selectedMeetingType?.name,
    error,
    brokerDataLoaded: !!brokerData
  })

  return (
    <div className={`max-w-4xl mx-auto ${className}`}>
      {/* Content - No scroll */}
      <div>
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-800 rounded-md text-sm">
            {error}
          </div>
        )}

        {/* Step 1: Meeting Type Selection */}
        {currentStep === 'meeting-type' && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              {t('appointment.selectMeetingType', 'Select Meeting Type')}
            </h3>
            <div className="space-y-2">
              {(() => {
                console.log('🔧 DEBUG - AppointmentBooking: Render - loading:', loading, 'meetingTypes.length:', meetingTypes.length, 'error:', error)
                return null
              })()}
              {loading ? (
                <div className="animate-pulse space-y-3">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-20 bg-gray-300 rounded-lg"></div>
                  ))}
                </div>
              ) : (
                meetingTypes.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => setSelectedMeetingType(type)}
                    className={`w-full p-3 text-left border-2 rounded-lg transition-colors ${
                      selectedMeetingType?.id === type.id
                        ? 'bg-gray-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    style={selectedMeetingType?.id === type.id ? {
                      borderColor: brokerData.color_scheme?.primary || '#3B82F6'
                    } : {}}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 text-sm">
                          {i18n.language === 'fr' && type.name_fr ? type.name_fr : type.name}
                        </h4>
                        <p className="text-xs text-gray-600 mt-1">
                          {i18n.language === 'fr' && type.description_fr ? type.description_fr : type.description}
                        </p>
                        <div className="flex items-center space-x-4 mt-1">
                          <span className="flex items-center text-xs text-gray-500">
                            <ClockIcon className="w-3 h-3 mr-1" />
                            {getDurationText(type.duration)}
                          </span>
                        </div>
                      </div>
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: type.color }}
                      />
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        )}

        {/* Step 2: Date & Time Selection */}
        {currentStep === 'date-time' && selectedMeetingType && (
          <div>
            <div className="mb-3">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                {t('appointment.selectDateTime', 'Select Date & Time')}
              </h3>
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: selectedMeetingType.color }}
                />
                <span>{selectedMeetingType.name}</span>
                <span>•</span>
                <span>{getDurationText(selectedMeetingType.duration)}</span>
              </div>
            </div>

            <div className="space-y-3">
              {/* Calendar - Compact */}
              <div className="w-full">
                <CalendlyStyleCalendar
                  onDateSelect={handleDateSelect}
                  selectedDate={selectedDate}
                  availableDates={availableDates}
                  isLoading={calendarLoading}
                  currentMonth={currentMonth}
                  onMonthChange={handleMonthChange}
                  colorScheme={brokerData.color_scheme}
                />
              </div>

              {/* Time Slots - Compact Grid */}
              <div className="w-full">
                {selectedDate ? (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2 text-center text-sm">
                      {t('appointment.availableTimes', 'Available Times')} - {formatDate(selectedDate)}
                    </h4>
                    <div className="grid grid-cols-5 gap-2">
                      {slotsLoading ? (
                        <>
                          {[1, 2, 3, 4, 5].map(i => (
                            <div key={i} className="h-9 bg-gray-300 rounded animate-pulse"></div>
                          ))}
                        </>
                      ) : availableSlots.length > 0 ? (
                        availableSlots.map((slot) => (
                          <button
                            key={slot}
                            onClick={() => setSelectedTime(slot)}
                            className={`h-9 px-1 text-center rounded border-2 transition-all bg-white flex items-center justify-center ${
                              selectedTime === slot
                                ? 'text-gray-800'
                                : 'border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                            }`}
                            style={selectedTime === slot ? {
                              borderColor: brokerData.color_scheme?.primary || '#3B82F6'
                            } : {}}
                          >
                            <span className="font-medium text-xs">{formatTime(slot)}</span>
                          </button>
                        ))
                      ) : (
                        <div className="col-span-full text-center py-4 text-gray-500">
                          <CalendarIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">{t('appointment.noAvailableTimes', 'No available times for this date')}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500">
                    <CalendarIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">{t('appointment.selectDatePrompt', 'Select a date to view available times')}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Contact Details */}
        {currentStep === 'details' && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              {t('appointment.yourDetails', 'Your Details')}
            </h3>
            
            {/* Meeting Summary */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-medium text-gray-900">
                    {i18n.language === 'fr' && selectedMeetingType?.name_fr ? selectedMeetingType.name_fr : selectedMeetingType?.name}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {selectedDate && formatDate(selectedDate)} {t('appointment.at', 'at')} {formatTime(selectedTime)}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('appointment.duration', 'Duration')}: {getDurationText(selectedMeetingType?.duration || 0)}
                  </p>
                </div>
                <div 
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: selectedMeetingType?.color }}
                />
              </div>
            </div>

            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    {i18n.language === 'fr' && brokerData?.first_name_label_fr 
                      ? brokerData.first_name_label_fr 
                      : brokerData?.first_name_label_en || t('appointment.firstName', 'First Name')
                    } *
                  </label>
                  <div className="relative">
                    <UserIcon className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    <input
                      type="text"
                      value={formData.customer_first_name}
                      onChange={(e) => setFormData({...formData, customer_first_name: e.target.value})}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:ring-2 focus:border-transparent"
                      placeholder={i18n.language === 'fr' && brokerData?.first_name_label_fr 
                        ? brokerData.first_name_label_fr 
                        : brokerData?.first_name_label_en || t('appointment.firstNamePlaceholder', 'First name')
                      }
                      required
                      onFocus={(e) => {
                        (e.target as HTMLInputElement).style.borderColor = brokerData.color_scheme?.primary || '#3B82F6'
                        ;(e.target as HTMLInputElement).style.boxShadow = `0 0 0 2px ${brokerData.color_scheme?.primary || '#3B82F6'}40`
                      }}
                      onBlur={(e) => {
                        (e.target as HTMLInputElement).style.borderColor = ''
                        ;(e.target as HTMLInputElement).style.boxShadow = ''
                      }}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    {i18n.language === 'fr' && brokerData?.last_name_label_fr 
                      ? brokerData.last_name_label_fr 
                      : brokerData?.last_name_label_en || t('appointment.lastName', 'Last Name')
                    } *
                  </label>
                  <div className="relative">
                    <UserIcon className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    <input
                      type="text"
                      value={formData.customer_last_name}
                      onChange={(e) => setFormData({...formData, customer_last_name: e.target.value})}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:ring-2 focus:border-transparent"
                      placeholder={i18n.language === 'fr' && brokerData?.last_name_label_fr 
                        ? brokerData.last_name_label_fr 
                        : brokerData?.last_name_label_en || t('appointment.lastNamePlaceholder', 'Last name')
                      }
                      required
                      onFocus={(e) => {
                        (e.target as HTMLInputElement).style.borderColor = brokerData.color_scheme?.primary || '#3B82F6'
                        ;(e.target as HTMLInputElement).style.boxShadow = `0 0 0 2px ${brokerData.color_scheme?.primary || '#3B82F6'}40`
                      }}
                      onBlur={(e) => {
                        (e.target as HTMLInputElement).style.borderColor = ''
                        ;(e.target as HTMLInputElement).style.boxShadow = ''
                      }}
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  {i18n.language === 'fr' && brokerData?.email_label_fr 
                    ? brokerData.email_label_fr 
                    : brokerData?.email_label_en || t('appointment.email', 'Email Address')
                  } *
                </label>
                <div className="relative">
                  <MailIcon className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  <input
                    type="email"
                    value={formData.customer_email}
                    onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:ring-2 focus:border-transparent"
                    placeholder={i18n.language === 'fr' && brokerData?.email_label_fr 
                      ? brokerData.email_label_fr 
                      : brokerData?.email_label_en || t('appointment.emailPlaceholder', 'Enter your email address')
                    }
                    required
                    onFocus={(e) => {
                      (e.target as HTMLInputElement).style.borderColor = brokerData.color_scheme?.primary || '#3B82F6'
                      ;(e.target as HTMLInputElement).style.boxShadow = `0 0 0 2px ${brokerData.color_scheme?.primary || '#3B82F6'}40`
                    }}
                    onBlur={(e) => {
                      (e.target as HTMLInputElement).style.borderColor = ''
                      ;(e.target as HTMLInputElement).style.boxShadow = ''
                    }}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  {i18n.language === 'fr' && brokerData?.phone_label_fr 
                    ? brokerData.phone_label_fr 
                    : brokerData?.phone_label_en || t('appointment.phone', 'Phone Number')
                  } *
                </label>
                <div className="relative">
                  <PhoneIcon className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  <input
                    type="tel"
                    value={formData.customer_phone}
                    onChange={(e) => setFormData({...formData, customer_phone: e.target.value})}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:ring-2 focus:border-transparent"
                    placeholder={i18n.language === 'fr' && brokerData?.phone_label_fr 
                      ? brokerData.phone_label_fr 
                      : brokerData?.phone_label_en || t('appointment.phonePlaceholder', 'Enter your phone number')
                    }
                    required
                    onFocus={(e) => {
                      (e.target as HTMLInputElement).style.borderColor = brokerData.color_scheme?.primary || '#3B82F6'
                      ;(e.target as HTMLInputElement).style.boxShadow = `0 0 0 2px ${brokerData.color_scheme?.primary || '#3B82F6'}40`
                    }}
                    onBlur={(e) => {
                      (e.target as HTMLInputElement).style.borderColor = ''
                      ;(e.target as HTMLInputElement).style.boxShadow = ''
                    }}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  {i18n.language === 'fr' && brokerData?.message_label_fr 
                    ? brokerData.message_label_fr 
                    : brokerData?.message_label_en || t('appointment.notes', 'Additional Notes (optional)')
                  }
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:ring-2 focus:border-transparent"
                  placeholder={i18n.language === 'fr' && brokerData?.message_placeholder_fr 
                    ? brokerData.message_placeholder_fr 
                    : brokerData?.message_placeholder_en || t('appointment.notesPlaceholder', 'Tell us about your mortgage needs or any questions you have...')
                  }
                  onFocus={(e) => {
                    (e.target as HTMLTextAreaElement).style.borderColor = brokerData.color_scheme?.primary || '#3B82F6'
                    ;(e.target as HTMLTextAreaElement).style.boxShadow = `0 0 0 2px ${brokerData.color_scheme?.primary || '#3B82F6'}40`
                  }}
                  onBlur={(e) => {
                    (e.target as HTMLTextAreaElement).style.borderColor = ''
                    ;(e.target as HTMLTextAreaElement).style.boxShadow = ''
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Confirmation */}
        {currentStep === 'confirmation' && (
          <div className="text-center">
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: `${brokerData.color_scheme?.primary || '#3B82F6'}20` }}
            >
              <svg 
                className="w-8 h-8" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
                style={{ color: brokerData.color_scheme?.primary || '#3B82F6' }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {t('appointment.appointmentBooked', 'Appointment Booked!')}
            </h3>
            <p className="text-gray-600 mb-6">
              {t('appointment.confirmationMessage', 'You\'ll receive a confirmation email shortly with all the meeting details and a calendar invite.')}
            </p>
            
            {/* Booking Summary */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h4 className="font-medium text-gray-900 mb-3">
                {t('appointment.meetingDetails', 'Meeting Details')}
              </h4>
              <div className="text-sm text-gray-600 space-y-2">
                <div className="flex justify-between">
                  <span>{t('appointment.meetingType', 'Meeting Type')}:</span>
                  <span className="font-medium">
                    {i18n.language === 'fr' && selectedMeetingType?.name_fr ? selectedMeetingType.name_fr : selectedMeetingType?.name}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>{t('appointment.dateTime', 'Date & Time')}:</span>
                  <span className="font-medium">
                    {selectedDate && formatDate(selectedDate)} at {formatTime(selectedTime)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>{t('appointment.duration', 'Duration')}:</span>
                  <span className="font-medium">{getDurationText(selectedMeetingType?.duration || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('appointment.with', 'With')}:</span>
                  <span className="font-medium">{brokerData.first_name} {brokerData.last_name}</span>
                </div>
              </div>
            </div>

            <p className="text-sm text-gray-500">
              {t('appointment.rescheduleInfo', 'Need to reschedule? Contact us or check your confirmation email for options.')}
            </p>
          </div>
        )}

        {/* Footer Actions */}
        {currentStep !== 'confirmation' && (
          <div className="pt-4 mt-4 border-t border-gray-200">
            <div className="flex justify-between">
              <button
                onClick={() => {
                  const steps = ['meeting-type', 'date-time', 'details', 'confirmation'] as const
                  const currentIndex = steps.indexOf(currentStep)
                  if (currentIndex > 0) {
                    setCurrentStep(steps[currentIndex - 1])
                  }
                }}
                className={`px-4 py-2 text-gray-600 hover:text-gray-900 ${
                  currentStep === 'meeting-type' ? 'invisible' : ''
                }`}
              >
                ← {t('appointment.back', 'Back')}
              </button>

              <button
                onClick={() => {
                  if (currentStep === 'details') {
                    handleBookAppointment()
                  } else {
                    const steps = ['meeting-type', 'date-time', 'details', 'confirmation'] as const
                    const currentIndex = steps.indexOf(currentStep)
                    if (currentIndex < steps.length - 1) {
                      setCurrentStep(steps[currentIndex + 1])
                    }
                  }
                }}
                disabled={!canProceedToNext() || loading}
                className="px-6 py-2 text-white rounded-md focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                style={{
                  backgroundColor: brokerData.color_scheme?.primary || '#3B82F6'
                }}
                onMouseEnter={(e) => {
                  const primaryColor = brokerData.color_scheme?.primary || '#3B82F6'
                  // Darken the color by 10% for hover
                  const darkerColor = primaryColor.replace('#', '').match(/.{1,2}/g)?.map((hex: string) => {
                    const num = parseInt(hex, 16)
                    const darker = Math.max(0, num - 25)
                    return darker.toString(16).padStart(2, '0')
                  }).join('') || primaryColor.replace('#', '')
                  ;(e.target as HTMLButtonElement).style.backgroundColor = `#${darkerColor}`
                }}
                onMouseLeave={(e) => {
                  ;(e.target as HTMLButtonElement).style.backgroundColor = brokerData.color_scheme?.primary || '#3B82F6'
                }}
                onFocus={(e) => {
                  ;(e.target as HTMLButtonElement).style.boxShadow = `0 0 0 2px ${brokerData.color_scheme?.primary || '#3B82F6'}40`
                }}
                onBlur={(e) => {
                  ;(e.target as HTMLButtonElement).style.boxShadow = ''
                }}
              >
                {loading ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>{currentStep === 'details' ? t('appointment.booking', 'Booking...') : t('appointment.loading', 'Loading...')}</span>
                  </div>
                ) : currentStep === 'details' ? t('appointment.bookAppointment', 'Book Appointment') : t('appointment.next', 'Next') + ' →'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default AppointmentBooking
